import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { AlertTriangle, TrendingUp, TrendingDown, Minus, HeartPulse, ChevronRight } from 'lucide-react';

interface VitalAlertItem {
  patient_id: string;
  patient_nickname: string;
  patient_photo_url: string | null;
  vital_type: string;
  last_value: number;
  last_value_secondary: number | null;
  unit: string | null;
  alert_count: number;
  trend: 'up' | 'down' | 'stable';
  last_alert_at: string;
}

const HOUR_OPTIONS = [24, 48, 72] as const;

const VITAL_TYPE_LABELS: Record<string, { pt: string; en: string }> = {
  blood_pressure: { pt: 'Pressão arterial', en: 'Blood pressure' },
  glucose: { pt: 'Glicose', en: 'Glucose' },
  temperature: { pt: 'Temperatura', en: 'Temperature' },
  oxygen_saturation: { pt: 'Saturação O₂', en: 'O₂ Saturation' },
  weight: { pt: 'Peso', en: 'Weight' },
  heart_rate: { pt: 'Freq. cardíaca', en: 'Heart rate' },
};

interface Props {
  companyId: string;
}

export function VitalAlertsPanel({ companyId }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [hours, setHours] = useState<number>(24);
  const [vitalFilter, setVitalFilter] = useState<string | null>(null);
  const isPt = i18n.language?.startsWith('pt');

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['vital-alerts-panel', companyId, hours],
    queryFn: async (): Promise<VitalAlertItem[]> => {
      // 1. Get company patients
      const { data: assignments } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (!assignments?.length) return [];

      const patientIds = assignments.map(a => a.patient_id);

      // 2. Get alert executions in the period
      const since = new Date();
      since.setHours(since.getHours() - hours);

      const { data: alertExecs } = await supabase
        .from('task_executions')
        .select('id, patient_id, vital_value, vital_value_secondary, executed_at, adhoc_vital_type, task_id')
        .in('patient_id', patientIds)
        .eq('is_alert', true)
        .gte('executed_at', since.toISOString())
        .order('executed_at', { ascending: false });

      if (!alertExecs?.length) return [];

      // 3. Get patient details
      const alertPatientIds = [...new Set(alertExecs.map(e => e.patient_id))];
      const { data: patients } = await supabase
        .from('patients')
        .select('id, nickname, photo_url')
        .in('id', alertPatientIds);

      const patientMap = Object.fromEntries((patients || []).map(p => [p.id, p]));

      // 4. Get task details for unit and vital_type
      const taskIds = [...new Set(alertExecs.filter(e => e.task_id).map(e => e.task_id!))];
      let taskMap: Record<string, { vital_type: string | null; unit: string | null }> = {};
      if (taskIds.length > 0) {
        const { data: tasks } = await supabase
          .from('care_plan_tasks')
          .select('id, vital_type, unit')
          .in('id', taskIds);
        if (tasks) {
          taskMap = Object.fromEntries(tasks.map(t => [t.id, { vital_type: t.vital_type, unit: t.unit }]));
        }
      }

      // 5. Group by patient + vital_type
      const grouped: Record<string, VitalAlertItem> = {};
      for (const exec of alertExecs) {
        const vt = exec.task_id && taskMap[exec.task_id]
          ? taskMap[exec.task_id].vital_type || 'unknown'
          : exec.adhoc_vital_type || 'unknown';
        const unit = exec.task_id && taskMap[exec.task_id]
          ? taskMap[exec.task_id].unit
          : null;

        const key = `${exec.patient_id}_${vt}`;
        if (!grouped[key]) {
          const patient = patientMap[exec.patient_id];
          grouped[key] = {
            patient_id: exec.patient_id,
            patient_nickname: patient?.nickname || '—',
            patient_photo_url: patient?.photo_url || null,
            vital_type: vt,
            last_value: exec.vital_value || 0,
            last_value_secondary: exec.vital_value_secondary,
            unit,
            alert_count: 0,
            trend: 'stable',
            last_alert_at: exec.executed_at,
          };
        }
        grouped[key].alert_count++;
      }

      // 6. Calculate trend (compare first and last values)
      for (const key of Object.keys(grouped)) {
        const [patientId, vitalType] = key.split('_');
        const execs = alertExecs.filter(
          e => e.patient_id === patientId &&
            ((e.task_id && taskMap[e.task_id]?.vital_type === vitalType) || e.adhoc_vital_type === vitalType)
        );
        if (execs.length >= 2) {
          const latest = execs[0].vital_value || 0;
          const oldest = execs[execs.length - 1].vital_value || 0;
          if (latest > oldest) grouped[key].trend = 'up';
          else if (latest < oldest) grouped[key].trend = 'down';
        }
      }

      return Object.values(grouped).sort((a, b) => b.alert_count - a.alert_count);
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  const vitalTypes = [...new Set(alerts.map(a => a.vital_type))];
  const filtered = vitalFilter ? alerts.filter(a => a.vital_type === vitalFilter) : alerts;

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === 'up') return <TrendingUp className="h-3.5 w-3.5 text-destructive" />;
    if (trend === 'down') return <TrendingDown className="h-3.5 w-3.5 text-success" />;
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const getVitalLabel = (type: string) => {
    const labels = VITAL_TYPE_LABELS[type];
    if (!labels) return type;
    return isPt ? labels.pt : labels.en;
  };

  const formatValue = (item: VitalAlertItem) => {
    const val = item.last_value_secondary
      ? `${item.last_value}x${item.last_value_secondary}`
      : `${item.last_value}`;
    return item.unit ? `${val} ${item.unit}` : val;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Period filter */}
      <div className="flex flex-wrap gap-2">
        {HOUR_OPTIONS.map(h => (
          <Button
            key={h}
            size="sm"
            variant={hours === h ? 'default' : 'outline'}
            onClick={() => setHours(h)}
            className="whitespace-nowrap"
          >
            {t('health.lastNHours', { count: h })}
          </Button>
        ))}
      </div>

      {/* Vital type filter */}
      {vitalTypes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setVitalFilter(null)}
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              !vitalFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {t('live.filterAll')}
          </button>
          {vitalTypes.map(vt => (
            <button
              key={vt}
              onClick={() => setVitalFilter(vt)}
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                vitalFilter === vt ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {getVitalLabel(vt)}
            </button>
          ))}
        </div>
      )}

      {/* Summary KPI */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-destructive" />
        </div>
        <div>
          <p className="text-2xl font-bold text-destructive">{alerts.length}</p>
          <p className="text-xs text-muted-foreground">
            {t('health.alertsInPeriod', { count: alerts.length })}
          </p>
        </div>
      </div>

      {/* Alert cards */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <HeartPulse className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>{t('health.noAlerts')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((item, idx) => (
            <Card
              key={`${item.patient_id}-${item.vital_type}-${idx}`}
              className="cursor-pointer hover:border-destructive/30 transition-colors"
              onClick={() => navigate(`/app/patients/${item.patient_id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <SignedAvatar
                    src={item.patient_photo_url}
                    bucket="patient-photos"
                    className="h-10 w-10"
                    fallback={
                      <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                        {item.patient_nickname.charAt(0).toUpperCase()}
                      </span>
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{item.patient_nickname}</p>
                      <Badge variant="destructive" className="text-[10px] px-1.5">
                        {item.alert_count}x
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-medium">{getVitalLabel(item.vital_type)}</span>
                      <span className="text-sm text-muted-foreground">·</span>
                      <span className="text-sm font-mono text-destructive">{formatValue(item)}</span>
                      <TrendIcon trend={item.trend} />
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
